import type { HTMLAttributes, ReactNode } from 'react';
import { gridBase, columnsRecipe, gapRecipe, allowedColumns, allowedGaps } from './Columns.css';

type ResponsiveNumber = number | { sm?: number; md?: number; lg?: number; xl?: number };

export type GridProps = Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className'> & {
    cols?: ResponsiveNumber;
    gap?: ResponsiveNumber;
    children?: ReactNode;
};

type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';
type BreakpointValues = Partial<Record<Breakpoint, number>>;
const bps: Breakpoint[] = ['sm', 'md', 'lg', 'xl'];

function normalize(value: ResponsiveNumber): BreakpointValues {
    if (typeof value === 'object' && value !== null) return value as BreakpointValues;
    return { sm: value as number };
}

function transform(allowed: readonly number[], value: BreakpointValues): Record<string, string> {
    const result: Record<string, string> = {};
    for (const bp of bps) {
        const v = value[bp];
        if (v === undefined) continue;
        if (allowed.includes(v)) {
            result[bp] = String(v);
        }
    }
    return result;
}

export function Columns({ children, gap = 8, cols = 3, ...rest }: GridProps) {
    const colValues = normalize(cols);
    const gapValues = normalize(gap);

    const colVariants = transform(allowedColumns, colValues);
    const gapVariants = transform(allowedGaps, gapValues);

    const colClass = columnsRecipe(colVariants);
    const gapClass = gapRecipe(gapVariants);

    return (
        <div className={`${gridBase} ${colClass} ${gapClass}`} {...rest}>
            {children}
        </div>
    );
}
